import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { ArrowLeft, ShieldCheck, Loader2, RefreshCw } from "lucide-react";
import { useAuth } from "../App";
import { VerificationActions } from "../components/VerificationActions";
import { VerificationImage } from "../components/VerificationImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusText = { approved: "Approved", denied: "Disapproved", on_hold: "On hold", scanning: "Scanning", under_review: "Under review" };

export default function AdminVerifications() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queueError, setQueueError] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState("");
  const selection = useRef(null);

  const fetchQueue = useCallback(async () => {
    if (!token) return;
    setLoading(true); setQueueError("");
    try {
      const { data } = await axios.get(`${API}/admin/verifications/queue`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(data.rows || []);
    } catch (_) { setQueueError("Could not load verification queue."); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const openDetail = async (identifier) => {
    if (!identifier) return;
    selection.current = identifier;
    setSelected(identifier); setDetail(null); setDetailError("");
    try {
      const { data } = await axios.get(`${API}/admin/verifications/${encodeURIComponent(identifier)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (selection.current === identifier) setDetail(data);
    } catch (_) {
      if (selection.current === identifier) setDetailError("Could not load this submission.");
    }
  };
  const deleted = () => {
    selection.current = null; setSelected(null); setDetail(null); fetchQueue();
  };
  const verdict = detail?.scans?.[0]?.verdict;
  if (!user) return null;

  return <div className="min-h-screen bg-black text-white pb-24" data-testid="admin-verifications-page">
    <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030]">
      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} aria-label="Back" className="w-9 h-9 shrink-0 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center" data-testid="admin-verif-back-btn"><ArrowLeft className="w-4 h-4" /></button>
        <Link to="/home" data-testid="admin-verif-home-link" className="flex items-center gap-2">
          <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" />
          <span className="hidden sm:inline font-bold">StardomFans · Admin</span>
        </Link>
        <h1 className="ml-auto text-base font-bold">Verification Portal</h1>
      </div>
    </header>
    <AdminNavigation />
    <main className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-[380px_minmax(0,1fr)] gap-6">
      <section className="min-w-0 rounded-lg border border-[#1A2030] bg-[#0A0E18] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#1A2030] flex items-center justify-between">
          <p className="text-xs text-[#8A92A0]" data-testid="verif-queue-count">{rows.length} submission{rows.length === 1 ? "" : "s"}</p>
          <button data-testid="verif-queue-refresh" onClick={fetchQueue} disabled={loading} aria-label="Refresh queue" title="Refresh queue" className="p-2 hover:text-[#EC008C] disabled:opacity-50"><RefreshCw className="w-4 h-4" /></button>
        </div>
        {queueError ? <p role="alert" data-testid="verif-queue-error" className="p-4 text-sm text-[#FF66BB]">{queueError}</p>
          : loading ? <p data-testid="verif-queue-loading" className="p-8 text-sm text-[#8A92A0]">Loading…</p>
          : !rows.length ? <p data-testid="verif-queue-empty" className="p-8 text-sm text-[#8A92A0]">No KYC submissions yet.</p>
          : <ul className="divide-y divide-[#1A2030]">{rows.map((row) => <li key={row.verification_id}>
            <button onClick={() => openDetail(row.verification_id)} data-testid={`verif-row-${row.verification_id}`}
              className={`w-full text-left px-4 py-3 hover:bg-[#0F1421] transition-colors ${selected === row.verification_id ? "bg-[#0F1421]" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p data-testid={`verif-name-${row.verification_id}`} className="text-sm font-medium break-words">{row.display_name}</p>
                  <p data-testid={`verif-identifier-${row.verification_id}`} className="text-[10px] text-[#8A92A0] font-mono break-all">{row.verification_id}</p>
                </div>
                <span data-testid={`verif-status-${row.verification_id}`} className={`shrink-0 text-[10px] rounded-full px-2 py-1 ${row.status === "approved" ? "text-[#8DB87A] bg-[#8DB87A]/10" : row.status === "denied" ? "text-[#FF66BB] bg-[#EC008C]/10" : "text-[#F0C464] bg-[#F0C464]/10"}`}>{statusText[row.status] || row.status}</span>
              </div>
            </button>
          </li>)}</ul>}
      </section>
      <section className="min-w-0 rounded-lg border border-[#1A2030] bg-[#0A0E18] p-5 min-h-[400px]" data-testid="verif-detail-panel">
        {!selected ? <div className="min-h-[300px] flex flex-col items-center justify-center text-center text-[#8A92A0] text-sm"><ShieldCheck className="w-10 h-10 mb-2" />Select a submission</div>
          : detailError ? <div><p role="alert" data-testid="verif-detail-error" className="text-sm text-[#FF66BB]">{detailError}</p><button data-testid="verif-detail-retry" onClick={() => openDetail(selected)} className="mt-3 underline">Retry</button></div>
          : !detail ? <p data-testid="verif-detail-loading" className="text-[#8A92A0] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading detail…</p>
          : <div className="space-y-4">
            <div><h2 data-testid="verif-detail-name" className="text-lg font-bold break-words">{detail.display_name}</h2><p data-testid="verif-detail-identifier" className="text-xs text-[#8A92A0] font-mono break-all">{detail.verification_id}</p><p data-testid="verif-detail-email" className="text-xs text-[#8A92A0] break-all">{detail.email}</p></div>
            {verdict && <div data-testid="verif-scan-verdict" className="border-t border-[#1A2030] pt-3 text-xs text-[#8A92A0] space-y-1">
              <h3 className="text-[#EC008C] font-bold mb-2">AI review</h3>
              <p>Deepfake confidence: {verdict.deepfake_confidence ?? "Unknown"}%</p><p>Faces match: {verdict.faces_match ? "Yes" : "No"}</p><p>ID appears authentic: {verdict.id_appears_authentic ? "Yes" : "No"}</p>
              {verdict.scan_error && <p data-testid="verif-scan-error" className="text-[#F0C464]">Automated review unavailable — manual review required.</p>}
            </div>}
            <div className="grid grid-cols-3 gap-2">
              <VerificationImage key={`${detail.verification_id}-front`} kind="id-front" label="ID front" url={detail.files.id_front_url} token={token} />
              <VerificationImage key={`${detail.verification_id}-back`} kind="id-back" label="ID back" url={detail.files.id_back_url} token={token} />
              <VerificationImage key={`${detail.verification_id}-selfie`} kind="selfie" label="Selfie" url={detail.files.selfie_url} token={token} />
            </div>
            <VerificationActions key={detail.verification_id} detail={detail} token={token} onDeleted={deleted} onUpdated={async () => { await fetchQueue(); await openDetail(selected); }} />
            {!!detail.events?.length && <div className="border-t border-[#1A2030] pt-4"><h3 className="text-xs text-[#8A92A0] mb-2">Audit trail</h3>
              <ul className="text-xs space-y-2">{detail.events.map((event) => <li key={event.row_id} data-testid={`verif-event-${event.row_id}`} className="break-words">
                <span className="text-[#F0C464]">{statusText[event.action] || event.action}</span><span className="text-[#8A92A0]"> · {(event.created_at || "").slice(0, 16)} · {event.admin_email || event.actor}{event.reason ? ` — ${event.reason}` : ""}</span>
              </li>)}</ul></div>}
          </div>}
      </section>
    </main>
  </div>;
}